import {
    IQueryParams,
    StoreCategory,
    searchStores,
} from "tsi-common-react/src/api/retail";
import { StoreSearchFilters } from "tsi-common-react/src/apps/retail/models";
import { ILocation, RetailStore } from "tsi-common-react/src/models/location";
import {
    ProductUUID,
    SyncStoreID,
    isoMiles,
} from "tsi-common-react/src/models/nominals";
import { check } from "tsi-common-react/src/models/utils";
import { fetchJson } from "tsi-common-react/src/utils/ajax";
import {
    isLatLngLocation,
    isZipLocation,
} from "tsi-common-react/src/utils/guards";
import { metersToMiles } from "tsi-common-react/src/utils/maps";

import { CMSFlagshipStorePage, CMSFlagshipStorePagesAPIPage } from "./models";

const buildBaseQuery = (location: ILocation): IQueryParams | null => {
    if (isLatLngLocation(location)) {
        return {
            origLat: location.lat,
            origLng: location.lng,
        };
    }
    // search by zip
    if (isZipLocation(location)) {
        return {
            q: location.zip,
        };
    }
    return null;
};

export const getStores = async (
    location: ILocation,
    filters: StoreSearchFilters,
    product?: ProductUUID | null,
) => {
    const baseQuery = buildBaseQuery(location);
    if (!baseQuery) {
        return {
            allStores: [],
            flagshipStores: [],
            storesWithProduct: [],
        };
    }
    // Convert from meters (internal representation) to miles for API
    const distanceMiles = isoMiles.unwrap(metersToMiles(filters.distance));
    const allStores = await searchStores({
        ...baseQuery,
        distance: distanceMiles,
        has_slot_tempur: "Y",
    });
    const flagshipStores = await searchStores({
        ...baseQuery,
        category: StoreCategory.FLAGSHIP,
        has_slot_tempur: "Y",
    });
    const storesWithProduct = product
        ? await searchStores({
              ...baseQuery,
              product: product,
              has_slot_tempur: "Y",
          })
        : [];
    return {
        allStores: allStores,
        flagshipStores: flagshipStores,
        storesWithProduct: storesWithProduct,
    };
};

export const getStoreByExternalID = async (
    location: ILocation,
    externalID: SyncStoreID,
): Promise<ReadonlyArray<RetailStore>> => {
    const baseQuery = buildBaseQuery(location);
    if (!baseQuery) {
        return [];
    }
    return searchStores({
        ...baseQuery,
        external_id: externalID,
    });
};

export const getFlagshipStorePage = async (
    externalID: SyncStoreID,
): Promise<CMSFlagshipStorePage> => {
    const resp = await fetchJson<unknown>(
        `/api/cms/v2/flagship-pages/${externalID}/`,
    );
    return check(CMSFlagshipStorePage.decode(resp));
};

export const getAllFlagshipStorePages = async (): Promise<
    CMSFlagshipStorePage[]
> => {
    const loadPage = async (offset = 0) => {
        const query = new URLSearchParams({ offset: String(offset) });
        const resp = await fetchJson<unknown>(
            `/api/cms/v2/flagship-pages/?${query}`,
        );
        return check(CMSFlagshipStorePagesAPIPage.decode(resp));
    };
    let results = await loadPage();
    let pages: CMSFlagshipStorePage[] = results.items;
    while (pages.length < results.meta.total_count) {
        results = await loadPage(pages.length);
        pages = pages.concat(results.items);
    }
    return pages;
};
